import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { ClipEdits } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { WordType } from '@goldcast/player-config/src/types/transcript-types';
import { approxGT, approxGTE, approxLT, approxLTE } from '@goldcast/player-config/src/utils/compareUtils';
import { getAllCompositeSegments, isClipInOriginalLanguage } from '@goldcast/player-config/src/utils/clipUtils';
import { fullTranscriptStore } from '@/stores/fullTranscriptStore';
import { addDecimalIfNeeded } from '@/libs/utils';

export interface SourceRange {
  start: number;
  end: number;
}

const isWithinRange = (startTime: number, endTime: number, range: SourceRange): boolean =>
  approxGTE(startTime, range.start) && approxLTE(endTime, range.end);

/**
 * The backend hides filler words (`word: ''` edits) only inside the clip's range at creation, so
 * filler words recovered from outside that range have no edit and would show in captions.
 * Adds hide edits for filler words that are inside `newRange` but not inside `oldRange`.
 * An existing edit for a word wins, as in the backend's `with_filler_hide_edits`.
 */
export function withRecoveredFillerHideEdits(
  edits: ClipEdits | undefined,
  oldRange: SourceRange,
  newRange: SourceRange
): ClipEdits {
  const currentEdits = edits ?? {};
  if (!approxLT(newRange.start, oldRange.start) && !approxGT(newRange.end, oldRange.end)) {
    return currentEdits;
  }

  const hideEdits: ClipEdits = {};
  for (const word of fullTranscriptStore.getSnapshot()) {
    const key = addDecimalIfNeeded(word.start_time);
    if (
      word.type === WordType.Pronunciation &&
      word.is_filler &&
      !currentEdits[key] &&
      isWithinRange(word.start_time, word.end_time, newRange) &&
      !isWithinRange(word.start_time, word.end_time, oldRange)
    ) {
      hideEdits[key] = { start_time: word.start_time, end_time: word.end_time, word: '' };
    }
  }

  return Object.keys(hideEdits).length ? { ...currentEdits, ...hideEdits } : currentEdits;
}

export function getCompositeSegmentRanges(clip: Clip): Record<string, SourceRange> {
  return (getAllCompositeSegments(clip) ?? []).reduce<Record<string, SourceRange>>((acc, segment) => {
    if (segment.segment_metadata) {
      acc[segment.id] = { start: segment.segment_metadata.start, end: segment.segment_metadata.end };
    }
    return acc;
  }, {});
}

/** Splits path: applies `withRecoveredFillerHideEdits` to every composite segment that existed before the update. */
export function addRecoveredFillerHideEditsToSegments(clip: Clip, oldRanges: Record<string, SourceRange>): void {
  if (!isClipInOriginalLanguage(clip)) {
    return;
  }
  (getAllCompositeSegments(clip) ?? []).forEach(segment => {
    const oldRange = oldRanges[segment.id];
    if (!oldRange || !segment.segment_metadata) {
      return;
    }
    const { start, end, edits } = segment.segment_metadata;
    segment.segment_metadata.edits = withRecoveredFillerHideEdits(edits, oldRange, { start, end });
  });
}

/** Non-splits path: the clip's range and edits live on `asset_metadata`. */
export function addRecoveredFillerHideEditsToClip(clip: Clip, oldRange: SourceRange): void {
  if (!isClipInOriginalLanguage(clip)) {
    return;
  }
  const { start, end, edits } = clip.asset_metadata;
  clip.asset_metadata.edits = withRecoveredFillerHideEdits(edits, oldRange, { start, end });
}
